import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FiSearch, FiShoppingBag } from 'react-icons/fi';
import merchantService from '../../services/merchant.service';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useLanguage } from '../../context/LanguageContext';
import { VerifiedCheck } from '../../components/merchant/VerifiedBadge';

const StoresPage = () => {
  const { t } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const [stores, setStores] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const fetchStores = async () => {
      setLoading(true);
      try {
        const params = { page, limit: 12 };
        if (search.trim()) params.search = search.trim();
        const { data } = await merchantService.listStores(params);
        setStores(data.data);
        setPagination(data.pagination);
      } catch (error) {
        console.error('Error fetching stores:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStores();
  }, [page, search]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    window.scrollTo(0, 0);
  };

  return (
    <div className="section-padding">
      <div className="container">
        <h2 className="mb-4">{t('browseStores')}</h2>

        {/* Search */}
        <div className="row mb-4">
          <div className="col-md-6">
            <form onSubmit={handleSearch}>
              <div className="input-group">
                <span className="input-group-text bg-white">
                  <FiSearch />
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder={t('searchStores')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <button className="btn btn-primary" type="submit">{t('search')}</button>
              </div>
            </form>
          </div>
        </div>

        {/* Store Grid */}
        {loading ? (
          <LoadingSpinner />
        ) : stores.length > 0 ? (
          <>
            <div className="row g-4">
              {stores.map((store) => (
                <div key={store._id} className="col-sm-6 col-md-4 col-lg-3">
                  <Link
                    to={`/stores/${store.storeSlug}`}
                    className="dashboard-card text-decoration-none text-dark d-block h-100"
                    style={{ transition: 'box-shadow 0.2s' }}
                    onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'}
                    onMouseLeave={(e) => e.currentTarget.style.boxShadow = ''}
                  >
                    <div className="text-center mb-3">
                      {store.logo ? (
                        <img
                          src={store.logo}
                          alt={store.storeName}
                          className="rounded-circle border"
                          style={{ width: '64px', height: '64px', objectFit: 'cover' }}
                        />
                      ) : (
                        <div
                          className="rounded-circle d-inline-flex align-items-center justify-content-center"
                          style={{
                            width: '64px',
                            height: '64px',
                            background: store.branding?.primaryColor || '#B91C1C',
                            color: '#fff',
                            fontSize: '24px',
                            fontWeight: 'bold',
                          }}
                        >
                          {store.storeName?.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>

                    <h5 className="text-center mb-1" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '2px' }}>
                      {store.storeName}
                      <VerifiedCheck verification={store.verification} size={14} />
                    </h5>

                    {store.description && (
                      <p className="text-muted small text-center mb-2" style={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}>
                        {store.description}
                      </p>
                    )}

                    {store.stats?.totalProducts > 0 && (
                      <p className="text-muted small text-center mb-0">
                        {t('xProducts', { count: store.stats.totalProducts })}
                      </p>
                    )}
                  </Link>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <nav className="mt-5 d-flex justify-content-center">
                <ul className="pagination">
                  <li className={`page-item ${page <= 1 ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => handlePageChange(page - 1)}>Previous</button>
                  </li>
                  {Array.from({ length: pagination.pages }, (_, i) => (
                    <li key={i + 1} className={`page-item ${page === i + 1 ? 'active' : ''}`}>
                      <button className="page-link" onClick={() => handlePageChange(i + 1)}>{i + 1}</button>
                    </li>
                  ))}
                  <li className={`page-item ${page >= pagination.pages ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => handlePageChange(page + 1)}>Next</button>
                  </li>
                </ul>
              </nav>
            )}
          </>
        ) : (
          <div className="text-center py-5">
            <FiShoppingBag size={64} className="text-muted mb-3" />
            <h4>{t('noStoresFound')}</h4>
            <p className="text-muted">
              {search ? 'Try a different search term.' : 'No stores are available yet.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default StoresPage;
